import {
	flatten,
	orderBy,
	findIndex,
	filter,
	map,
	uniqBy,
	keyBy,
	find,
	forEach,
	some
} from "lodash";
import template from "./report-form.html";
import field from "./report-field";
import activityTriggerActions from "./activity-trigger-actions";
import "./report-form.less";
import { ActivityTriggerCoordinator } from "../../services/activity-trigger";
import {
	EVENT_FILTER_CURRENT_OPTIONS,
	EVENT_REMOVE_OPTIONS,
	EVENT_VALUE_CHANGED,
	EVENT_CLEAR_VALUE,
	PIVOT_TYPES,
	EVENT_CLEAR_ROW,
	EVENT_EXPAND_MULTI_ENTRY_LIST,
	EVENT_SECTION_FIELD_VISIBILTY_CHANGE,
	EVENT_ENABLE_SUCCESSOR_LOCK,
	EVENT_DISABLE_SUCCESSOR_LOCK,
	EVENT_FILTER_BY_DETAILS,
	EVENT_TRIGGER_FILTER_RULES,
	EVENT_FILTER_ALL_OPTIONS
} from "constants.js";
import swal from "sweetalert2";

export function controller($scope, $logger, appState, gpsService, toastr) {
	const ctrl = this;
	const fieldControllers = [];

	ctrl.pivotTypes = PIVOT_TYPES;

	ctrl.$onInit = onInit;
	ctrl.registerFieldController = registerFieldController;
	ctrl.isPivotExpanded = false;
	ctrl.expandPivot = expandPivot;
	ctrl.disableMultiEntryCollapse = disableMultiEntryCollapse;
	ctrl.showTriggerWarning = showTriggerWarning;
	ctrl.clearPivoted = clearPivoted;
	ctrl.showLocks = ctrl.showLocks === undefined ? true : ctrl.showLocks;
	ctrl.getValues = getFieldValues;
	ctrl.getActivityTriggerFieldActions = getActivityTriggerFieldActions;
	ctrl.getActivityTriggerSectionActions = getActivityTriggerSectionActions;
	ctrl.hasAvailableActivityTriggerFieldAction = hasAvailableActivityTriggerFieldAction;
	ctrl.isActivityTriggerActionAvailable = isActivityTriggerActionAvailable;
	ctrl.runActivityTriggerAction = runActivityTriggerAction;
	ctrl.noLabelTemplates = {
		"Markdown": true,
		"EntityContactSelector": true
	};
	ctrl.emptyLabelTemplates = {
		"ReadOnlyTextInput": true
	};
	ctrl.pivotedFieldValueChangeRegistrations = [];

	ctrl.pivotEvent = $logger.buildReportEvent("pivot");

	ctrl.dirtyFields = {};

	ctrl.hideTooltipFieldTypes = {
		Checkbox: true
	};

	(ctrl.scope || {}).getForm = () => $scope[ctrl.formIdentifier];

	function onInit() {
		ctrl.eventBus = ctrl.reportScope.events;

		if(ctrl.registerValueProvider) {
			ctrl.registerValueProvider({ getValues: getFieldValues });
		}

		// IsNotReported: field is hidden and value is saved to api
		// IsHidden: field is hidden but value is saved to api
		filter(ctrl.fields, f => f.IsNotReported === true || f.IsHidden === true).map(field => (field.visible = false));
		ctrl.staticFields = ctrl.fields.filter(field => !field.Pivot);

		ctrl.sections = [];

		const staticSectionedFields = filter(ctrl.staticFields, f => !!f.Section);
		const staticUnsectionedFields = filter(ctrl.staticFields, f => !f.Section);

		const noSection = {
			id: 0,
			fields: staticUnsectionedFields,
			pivotedFields: [],
			isVisible: staticUnsectionedFields.length > 0
		};

		if(ctrl.staticFields.length > 0 && staticSectionedFields.length > 0) {
			ctrl.sections = orderBy(
				map(
					uniqBy(staticSectionedFields, d => d.Section.Id),
					function(item) {
						const thisSection = {
							id: item.Section.Id,
							name: item.Section.Name,
							order: item.Section.Order,
							fields: filter(staticSectionedFields, f => f.Section.Id === item.Section.Id),
							pivotedFields: []
						};

						thisSection.isVisible = thisSection.fields.length > 0 && some(thisSection.fields, f => f.visible !== false);

						ctrl.eventBus.register(
							EVENT_SECTION_FIELD_VISIBILTY_CHANGE(thisSection.id),
							() => {
								thisSection.isVisible = thisSection.fields.length > 0 && some(thisSection.fields, f => f.visible !== false);
							},
							$scope
						);

						return thisSection;
					}
				),
				["order"]
			);
		}

		ctrl.sections.push(noSection);

		gatherPivotFields();
		ctrl.activityTriggerCoordinator = new ActivityTriggerCoordinator({
			features: ctrl.features,
			fields: ctrl.fields,
			getControllers: () => fieldControllers,
			getCurrentPosition: () => gpsService.getCurrentPosition(),
			isReadonly: () => ctrl.readonly,
			logger: $logger
		});

		ctrl.primaryPivotType && ctrl.eventBus.register(EVENT_EXPAND_MULTI_ENTRY_LIST, (e, params) => expandPivot(true, params.sectionId), $scope);
	}

	function getActivityTriggerFieldActions(fieldId) {
		return ctrl.activityTriggerCoordinator?.getFieldActions(fieldId) ?? [];
	}

	function getActivityTriggerSectionActions(sectionId) {
		return ctrl.activityTriggerCoordinator?.getSectionActions(sectionId) ?? [];
	}

	function hasAvailableActivityTriggerFieldAction(fieldId) {
		return getActivityTriggerFieldActions(fieldId).some(isActivityTriggerActionAvailable);
	}

	function isActivityTriggerActionAvailable(action) {
		return ctrl.activityTriggerCoordinator?.isAvailable(action) === true;
	}

	async function runActivityTriggerAction(action) {
		try {
			return await ctrl.activityTriggerCoordinator.apply(action);
		}
		catch(error) {
			$logger.exception(error, "Unable to capture activity trigger values", { action });
			toastr.error("Unable to capture the activity values. Check required browser permissions and try again.");
			return false;
		}
		finally {
			$scope.$evalAsync();
		}
	}

	ctrl.toggleFieldLock = (field) => {
		field.isLocked = !field.isLocked;

		const event = field.isLocked ? EVENT_ENABLE_SUCCESSOR_LOCK(field.Id, undefined) : EVENT_DISABLE_SUCCESSOR_LOCK(field.Id, undefined);
		ctrl.eventBus.trigger(event);
	};

	ctrl.setFieldDirty = (fieldId, isDirty) => {
		const dirtyFields = {
			...ctrl.dirtyFields
		};

		if(isDirty) {
			dirtyFields[fieldId] = true;
		}
		else if(!!dirtyFields[fieldId]) {
			delete dirtyFields[fieldId];
		}

		ctrl.dirtyFields = dirtyFields;
	};

	function gatherPivotFields() {
		ctrl.pivotFields = ctrl.fields
			.filter(field => !!field.Pivot)
			.reduce((acc, curr) => {
				let sectionId = 0;
				if(curr.Section) {
					if(!ctrl.sections.some(s => s.id === curr.Section.Id)) {
						const thisSection = {
							id: curr.Section.Id,
							name: curr.Section.Name,
							order: curr.Section.Order,
							isVisible: true,
							fields: [],
							pivotedFields: [curr]
						};

						ctrl.sections.push(thisSection);
					}
					else {
						const thisSection = ctrl.sections.find(s => s.id === curr.Section.Id);
						thisSection.pivotedFields.push(curr);
					}
					sectionId = curr.Section.Id;
				}

				if(!acc) {
					acc = {};
				}

				if(!acc[sectionId]) {
					acc[sectionId] = {};
				}

				if(!acc[sectionId][curr.Pivot]) {
					acc[sectionId][curr.Pivot] = [];
				}

				//const thisFieldValue = find(ctrl.values, val => val.FieldId === curr.Id);

				//hasPivotValue = hasPivotValue || !!thisFieldValue;

				if(curr.Pivot === ctrl.pivotTypes.Repeat) {
					acc[sectionId][curr.Pivot].push(curr);
					return acc;
				}

				ctrl.primaryPivotType = curr.Pivot;
				acc[sectionId][curr.Pivot] = curr;

				return acc;
			}, {});

		ctrl.allowPivot = !ctrl.detailId;
		ctrl.primaryPivotIds = {};

		forEach(ctrl.sections, sec => {
			if(ctrl.pivotFields[sec.id]) {
				ctrl.primaryPivotType && setupVaryByField(ctrl.pivotFields[sec.id][ctrl.primaryPivotType], sec.id);
				ctrl.primaryPivotType && (ctrl.primaryPivotIds[sec.id] = [undefined]);
				ctrl.primaryPivotType && (ctrl.pivotFields[sec.id][ctrl.primaryPivotType].Template = "Selection");
				ctrl.primaryPivotType && (ctrl.pivotFields[sec.id][ctrl.primaryPivotType].allowPivot = ctrl.allowPivot);

				ctrl.primaryPivotType &&
					ctrl.eventBus.register(
						EVENT_CLEAR_VALUE(ctrl.pivotFields[sec.id][ctrl.primaryPivotType].Id),
						() => {
							if(ctrl.isPivotExpanded) {
								clearPivoted(sec.id);
								expandPivot(false, sec.id);
							}
						},
						$scope
					);
			}
		});
	}

	function filterVaryByField(params, pivotField, sectionId) {
		if(params.ids) {
			const fullPivotOptions = params.ids.map(id => pivotField.dataDictionary[id]);
			const orderedPivotOptions = orderBy(fullPivotOptions, ["Order"]);
			ctrl.primaryPivotOptionIds[sectionId] = orderedPivotOptions.map(o => o.Id);

			if(ctrl.isPivotExpanded) {
				if(ctrl.primaryPivotOptionIds[sectionId] && ctrl.primaryPivotOptionIds[sectionId].length < 2) {
					expandPivot(false, sectionId);
					$scope.$$postDigest(() => {
						ctrl.eventBus.trigger(EVENT_FILTER_CURRENT_OPTIONS(pivotField.Id), { ids: params.ids });
					});
				} else {
					resetValueChangedRegistrations();
					handleExpandedPivot(sectionId);
				}
			}
		}
	}

	function setupVaryByField(pivotField, sectionId) {
		configureTriggerWarnings(pivotField);

		if(!ctrl.primaryPivotOptionIds) {
			ctrl.primaryPivotOptionIds = {};
		}

		if(ctrl.primaryPivotType === ctrl.pivotTypes.RecentlyUsed) {
			const recentItems = pivotField.Data.filter(item => item.IsRecent);
			ctrl.primaryPivotOptionIds[sectionId] = recentItems.map(data => data.Id);
		} else {
			ctrl.primaryPivotOptionIds[sectionId] = pivotField.Data.map(data => data.Id);
		}

		pivotField.dataDictionary = keyBy(pivotField.Data, item => item.Id);

		ctrl.eventBus.register(
			EVENT_FILTER_CURRENT_OPTIONS(pivotField.Id),
			(e, params) => {
				filterVaryByField(params, pivotField, sectionId);
			},
			$scope
		);

		ctrl.eventBus.register(
			EVENT_FILTER_ALL_OPTIONS(pivotField.Id),
			(e, params) => {
				filterVaryByField(params, pivotField, sectionId);
			},
			$scope
		);

		ctrl.eventBus.register(
			EVENT_REMOVE_OPTIONS(pivotField.Id),
			(e, params) => {
				ctrl.primaryPivotOptionIds[sectionId] = ctrl.primaryPivotOptionIds[sectionId].filter(optionId => params.items.indexOf(optionId) < 0);

				if(ctrl.isPivotExpanded) {
					if(ctrl.primaryPivotOptionIds[sectionId] && ctrl.primaryPivotOptionIds[sectionId].length < 2) {
						expandPivot(false, sectionId);
					} else {
						resetValueChangedRegistrations();
						handleExpandedPivot(sectionId);
					}
				}
			},
			$scope
		);
	}

	function configureTriggerWarnings(baseField) {
		if(baseField.triggerFieldId) {
			const triggerField = find(ctrl.fields, field => field.Id == baseField.triggerFieldId);

			if(triggerField) {
				triggerField.warnBeforeChangeTrigger = true;
				configureTriggerWarnings(triggerField);
			}
		}
	}

	async function showTriggerWarning(field) {
		if(!ctrl.isPivotExpanded || !ctrl.disableMultiEntryButton)
			return true;

		const userAction = await swal({
			title: "Warning!",
			html: `Changing the value of the ${field.Name} field will clear ALL currently entered multi-entry values from the form.
						<br/>
						<br/>
						Are you sure you want to do that?`,
			showCloseButton: true,
			type: "warning",
			showCancelButton: true,
			confirmButtonColor: "#8CC541",
			confirmButtonText: "Yes, do it!"
		})
			.catch(err => $logger.error(err));

		if(userAction.dismiss) {
			return false;
		}

		const sectionId = (!!field.Section && field.Section.Id) || 0;
		const repeatFields = ctrl.pivotFields[sectionId][ctrl.pivotTypes.Repeat];
		forEach(ctrl.primaryPivotOptionIds, optionId => {
			forEach(repeatFields, field => {
				ctrl.eventBus.trigger(EVENT_CLEAR_VALUE(field.Id, optionId));
			});
		});

		ctrl.disableMultiEntryButton = false;

		return true;
	}

	function handleExpandedPivot(sectionId) {
		ctrl.primaryPivotIds[sectionId] = ctrl.primaryPivotOptionIds[sectionId];

		const triggerFieldId = ctrl.pivotFields[sectionId][ctrl.primaryPivotType].Id;

		$scope.$$postDigest(() => {
			const triggeringControllers = fieldControllers.filter(c => c.field.Id === triggerFieldId);
			forEach(triggeringControllers, c => c.handleValueChanged());
		});
	}

	function resetValueChangedRegistrations() {
		ctrl.pivotedFieldValueChangeRegistrations.forEach(r => ctrl.eventBus.unregister(r));
		ctrl.pivotedFieldValueChangeRegistrations = [];

		Object.entries(ctrl.primaryPivotOptionIds).forEach(([sectionId, repeatIdsForSection]) => {
			const primaryPivotFieldForSection = ctrl.pivotFields[sectionId][ctrl.primaryPivotType];
			const thisRepeatFieldsForSection = ctrl.pivotFields[sectionId][ctrl.pivotTypes.Repeat];
			const allPivotFields = [primaryPivotFieldForSection, ...thisRepeatFieldsForSection];

			repeatIdsForSection.forEach(repeatId => {
				allPivotFields
					.forEach(field => {
						const registration = ctrl.eventBus.register(EVENT_VALUE_CHANGED(field.Id, repeatId),
							() => {
								ctrl.eventBus.trigger(EVENT_TRIGGER_FILTER_RULES, {
									field,
									repeatId
								});
							},
							$scope);

						ctrl.pivotedFieldValueChangeRegistrations.push(registration);
					});
			});
		});
	}

	function expandPivot(open, sectionId) {
		ctrl.isPivotExpanded = open !== undefined ? open : !ctrl.isPivotExpanded;
		const formCode = appState.forms.selected.Code;

		if(ctrl.isPivotExpanded) {
			resetValueChangedRegistrations();

			handleExpandedPivot(sectionId);
			$logger.event(ctrl.pivotEvent("opened"), { sectionId, formCode });
		} else {
			const triggerField = ctrl.pivotFields[sectionId][ctrl.primaryPivotType];

			ctrl.primaryPivotIds[sectionId] = [undefined];
			ctrl.pivotFields[sectionId][ctrl.primaryPivotType].Template = "Selection";

			ctrl.pivotedFieldValueChangeRegistrations.forEach(r => ctrl.eventBus.unregister(r));
			ctrl.pivotedFieldValueChangeRegistrations = [];

			$logger.event(ctrl.pivotEvent("closed"), { sectionId, formCode });

			$scope.$$postDigest(() => {
				ctrl.eventBus.trigger(EVENT_VALUE_CHANGED(triggerField.Id), {
					field: triggerField,
					value: undefined,
					refilterThis: true
				});
				ctrl.eventBus.trigger(EVENT_FILTER_BY_DETAILS(triggerField.Id));
			});
		}
	}

	function clearPivoted(sectionId) {
		forEach(ctrl.primaryPivotIds[sectionId], id => {
			ctrl.eventBus.trigger(EVENT_CLEAR_ROW(id));
		});

		ctrl.disableMultiEntryButton = false;
	}

	function disableMultiEntryCollapse() {
		ctrl.disableMultiEntryButton = true;
	}

	function getFieldValues(api = false) {
		const results = fieldControllers
			.filter(fc => fc.field.visible !== false || fc.field.IsHidden || (fc.field.IsNotReported && api !== true))
			.map(x => {
				try {
					const val = x.getCurrentValue(api);
					forEach(val, v => {
						if(v.IsNotAvailable === false) {
							delete v.IsNotAvailable;
						}
						if(ctrl.detailId !== undefined) {
							v.DetailId = ctrl.detailId;
						}
					});

					return val;
				}
				catch(err) {
					$logger.exception(err, "Error getting field values", { fieldController: x });

					return "error";
				}
			});
		let flattened;

		if(some(results, r => r === "error")) {
			flattened = null;
		} else {
			flattened = flatten(results);
		}
		return flattened;
	}

	function registerFieldController(fieldController) {
		fieldControllers.push(fieldController);
		return function unregister() {
			const idx = findIndex(fieldControllers, x => x === fieldController);
			if(idx !== -1) {
				fieldControllers.splice(idx, 1);
			}
		};
	}
}

const deps = [field, activityTriggerActions];

export default angular
	.module("vesl.components.report-form", deps)
	.component("reportForm", {
		template,
		controller,
		transclude: true,
		bindings: {
			fields: "<",
			values: "<",
			detailId: "<",
			reportId: "<",
			features: "<",
			registerValueProvider: "&",
			getValues: "=",
			scope: "<",
			readonly: "<",
			name: "<",
			formIdentifier: "@",
			showLocks: "<",
			dirtyFields: "="
		},
		require: {
			reportScope: "^^reportScope"
		}
	}).name;
